import React, { createContext, useContext, useState, useEffect } from "react";

export type Locale = "en" | "zh" | "ms";

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextType>({
  locale: "en",
  setLocale: () => {},
  t: (key) => key,
});

export const useI18n = () => useContext(I18nContext);

export const localeLabels: Record<Locale, string> = {
  en: "EN",
  zh: "中文",
  ms: "BM",
};

const translations: Record<Locale, Record<string, string>> = {
  en: {
    // Header
    "nav.features": "Features",
    "nav.pricing": "Pricing",
    "nav.howItWorks": "How It Works",
    "nav.login": "Login",

    // Hero
    "hero.badge": "🇲🇾 Built for Malaysian Businesses — Start Free Today",
    "hero.title1": "Meet",
    "hero.title2": "Answer Customers 24/7, Automatically",
    "hero.description": "Your AI chatbot that answers customers in English, BM & 中文 — while you sleep. Upload FAQs, embed one script tag, and go live in 5 minutes. No coding needed.",
    "hero.cta1": "Get Started Free",
    "hero.cta2": "See How It Works",
    "hero.chat.title": "Onyx AI Chat",
    "hero.chat.q1": "What are your business hours?",
    "hero.chat.a1": "We're open Monday to Friday, 9 AM - 6 PM (MYT). For urgent matters, our AI assistant is available 24/7!",
    "hero.chat.q2": "How do I return a product?",
    "hero.chat.a2": "You can initiate a return within 30 days of purchase. Simply go to your order history and click \"Request Return\" on the item.",
    "hero.trusted": "Trusted by SMEs across Malaysia",
    "hero.demo.label": "Live Demo",
    "hero.demo.prompt": "Try chatting with Onyx AI right now — ask anything!",
    "hero.demo.tryIt": "Live Demo — See Onyx AI in Action",
    "hero.demo.inputPlaceholder": "Ask me anything about Onyx AI...",

    // Stats
    "stats.businesses": "Businesses Served",
    "stats.messages": "Messages Answered",
    "stats.uptime": "Uptime",
    "stats.satisfaction": "Satisfaction Rate",

    // How It Works
    "howItWorks.title": "Get Started in Minutes",
    "howItWorks.subtitle": "Three simple steps to transform your customer support with AI — no coding required.",
    "howItWorks.step1.label": "Step 1",
    "howItWorks.step1.title": "Upload Your FAQs",
    "howItWorks.step1.description": "Import your existing FAQ knowledge base via CSV or add entries manually. Our AI learns your business instantly — from product details to return policies.",
    "howItWorks.step2.label": "Step 2",
    "howItWorks.step2.title": "Customize Your Bot",
    "howItWorks.step2.description": "Set your brand colors, logo, welcome message, and AI persona. Make it feel like a natural extension of your brand identity.",
    "howItWorks.step3.label": "Step 3",
    "howItWorks.step3.title": "Embed & Go Live",
    "howItWorks.step3.description": "Copy a single script tag into your website. Your AI chatbot is live and ready to serve customers 24/7 in multiple languages.",

    // Features
    "features.title": "Everything You Need to Automate Support",
    "features.subtitle": "A complete AI chatbot platform built for Malaysian businesses — from kedai runcit to enterprise. No technical expertise required.",
    "features.faq.title": "FAQ-Powered AI",
    "features.faq.description": "Feed in your FAQ knowledge base and our AI instantly learns to answer customer questions accurately and naturally.",
    "features.branding.title": "White-Label Branding",
    "features.branding.description": "Customize colors, logos, and messaging to match your brand. Your customers see your brand, not ours.",
    "features.streaming.title": "Streaming Responses",
    "features.streaming.description": "Real-time streamed AI responses with Server-Sent Events. Your customers see answers appearing word by word.",
    "features.multiTenant.title": "Multi-Tenant Architecture",
    "features.multiTenant.description": "Each client gets isolated data, unique API keys, and custom configurations. Perfect for agencies and resellers.",
    "features.embed.title": "Easy Embed Integration",
    "features.embed.description": "Add the chatbot to any website with a single script tag. Works with Shopify, WordPress, Wix, or any custom site.",
    "features.analytics.title": "Analytics Dashboard",
    "features.analytics.description": "Track chat sessions, popular questions, and response quality. Understand what your customers are asking about most.",
    "features.liveAgent.title": "Live Agent Handoff",
    "features.liveAgent.description": "Seamlessly transfer complex conversations to human agents. Your team gets full context and chat history.",
    "features.multilingual.title": "Multilingual Support",
    "features.multilingual.description": "Serve customers in English, Bahasa Melayu, and 中文. The AI responds in the language your customers prefer.",
    "features.hours.title": "Business Hours & Auto-Reply",
    "features.hours.description": "Set operating hours and auto-reply messages. Customers always know when to expect a human response.",
    "features.agentWidget.title": "Embeddable Agent Widget",
    "features.agentWidget.description": "Let your CS team respond to customers from any system. Embed a lightweight agent console into your CRM, helpdesk, or internal tools with one script tag.",
    "features.notifications.title": "Real-Time Notifications",
    "features.notifications.description": "Never miss a customer message. Get sound alerts, desktop notifications, and tab title flashing when new chats or messages arrive.",

    // Use Cases
    "useCases.title": "Built for Malaysian Businesses",
    "useCases.subtitle": "Whether you run a kedai, clinic, or tech startup — Onyx AI adapts to your industry and speaks your customers' language.",
    "useCases.ecommerce.title": "E-Commerce & Retail",
    "useCases.ecommerce.description": "Handle product inquiries, order status, returns, and shipping questions automatically. Perfect for Shopee and Lazada sellers who need 24/7 automated customer support.",
    "useCases.fnb.title": "F&B & Restaurants",
    "useCases.fnb.description": "Answer menu questions, take reservations, and share operating hours. Serve your customers even after your kitchen closes — the best AI chatbot for restaurants in Malaysia.",
    "useCases.services.title": "Professional Services",
    "useCases.services.description": "Law firms, clinics, agencies — let AI handle appointment scheduling, service inquiries, and common FAQs. Reduce no-shows and phone calls.",
    "useCases.education.title": "Education & Training",
    "useCases.education.description": "Tuition centres, universities, and online courses — automate enrollment queries, class schedules, and fee inquiries for parents and students.",

    // Pricing
    "pricing.title": "Simple, Transparent Pricing",
    "pricing.subtitle": "Start free and scale as you grow. No hidden fees, no surprises. Affordable chatbot solutions for Malaysian SMEs.",
    "pricing.free.name": "Free",
    "pricing.free.description": "Perfect for trying out AI-powered support",
    "pricing.free.f1": "100 messages / month",
    "pricing.free.f2": "1 live agent",
    "pricing.free.f3": "AI chatbot with persona",
    "pricing.free.f4": "Basic analytics",
    "pricing.free.f5": "Community support",
    "pricing.free.cta": "Get Started",
    "pricing.starter.name": "Starter",
    "pricing.starter.description": "For small businesses getting started",
    "pricing.starter.f1": "5,000 messages / month",
    "pricing.starter.f2": "Up to 3 live agents",
    "pricing.starter.f3": "Custom AI persona name",
    "pricing.starter.f4": "Custom branding",
    "pricing.starter.f5": "CSV bulk import",
    "pricing.starter.f6": "Priority support",
    "pricing.starter.f7": "Sound & desktop notifications",
    "pricing.starter.cta": "Get Started",
    "pricing.pro.name": "Pro",
    "pricing.pro.description": "For growing businesses that need more",
    "pricing.pro.f1": "20,000 messages / month",
    "pricing.pro.f2": "Up to 10 live agents",
    "pricing.pro.f3": "Advanced analytics",
    "pricing.pro.f4": "Business hours",
    "pricing.pro.f5": "Canned responses",
    "pricing.pro.f6": "Custom system prompt",
    "pricing.pro.f7": "Sound & desktop notifications",
    "pricing.pro.f8": "Embeddable agent widget",
    "pricing.pro.cta": "Start Free Trial",
    "pricing.pro.badge": "Most Popular",
    "pricing.business.name": "Business",
    "pricing.business.description": "For larger teams with high-volume needs",
    "pricing.business.f1": "50,000 messages / month",
    "pricing.business.f2": "Unlimited live agents",
    "pricing.business.f3": "Unlimited FAQs",
    "pricing.business.f4": "Skill-based routing",
    "pricing.business.f5": "Dedicated support",
    "pricing.business.f6": "API rate limit customization",
    "pricing.business.f7": "Sound & desktop notifications",
    "pricing.business.f8": "Embeddable agent widget",
    "pricing.business.cta": "Contact Sales",
    "pricing.perMonth": "/month",
    "pricing.seeFullPricing": "See full pricing →",
    "pricing.faq.title": "Frequently Asked Questions",
    "pricing.faq.q1": "Can I change plans later?",
    "pricing.faq.a1": "Yes, you can upgrade or downgrade anytime from your dashboard. Changes take effect immediately.",
    "pricing.faq.q2": "Is there a free trial?",
    "pricing.faq.a2": "The Free plan is always free — no credit card required. Paid plans include a 7-day money-back guarantee.",
    "pricing.faq.q3": "What payment methods do you accept?",
    "pricing.faq.a3": "We accept credit/debit cards and FPX online banking for Malaysian customers. Prices shown in RM are approximate based on current exchange rates.",
    "pricing.faq.q4": "What happens when I exceed my message limit?",
    "pricing.faq.a4": "Your chatbot will pause until the next billing cycle. You can upgrade anytime to restore service immediately.",
    "pricing.faq.q5": "Do you offer refunds?",
    "pricing.faq.a5": "Yes, we offer a 7-day money-back guarantee on all paid plans. Contact us at hello.onyx@cloudforge.com.my.",

    // CTA
    "cta.title": "Ready to Transform Your Customer Support?",
    "cta.subtitle": "Join hundreds of Malaysian businesses already using Onyx AI to delight their customers — in every language they speak.",
    "cta.button": "Start Free — No Credit Card Required",

    // Affiliate
    "affiliate.badge": "Earn With Onyx AI",
    "affiliate.title": "Become an Onyx AI Affiliate & Earn Recurring Commissions",
    "affiliate.description": "Join our affiliate program and earn money every time someone you refer subscribes. It's the easiest way to monetize your network — whether you're an agency, influencer, or tech enthusiast.",
    "affiliate.perk1": "20% recurring commission on every referral",
    "affiliate.perk2": "Real-time dashboard to track referrals & earnings",
    "affiliate.perk3": "Custom referral links and promotional materials",
    "affiliate.perk4": "Monthly payouts — no minimum threshold",
    "affiliate.cta": "Join Affiliate Program",
    "affiliate.dashboard": "Affiliate Dashboard",
    "affiliate.thisMonth": "This Month",
    "affiliate.referrals": "Referrals",
    "affiliate.earnings": "Earnings",

    // Testimonials
    "testimonials.title": "Loved by Malaysian Businesses",
    "testimonials.subtitle": "See what our users say about automating their customer support with Onyx AI.",
    "testimonials.1.name": "Sarah Lim",
    "testimonials.1.role": "E-Commerce Store Owner, KL",
    "testimonials.1.quote": "Onyx AI cut our support tickets by 70%. Our Shopee customers get instant answers in Chinese and English — even at 3am. Game changer for our small team.",
    "testimonials.2.name": "Ahmad Razak",
    "testimonials.2.role": "Restaurant Manager, Penang",
    "testimonials.2.quote": "Pelanggan kami suka boleh tanya soalan dalam BM dan dapat jawapan segera. Menu, reservasi, waktu buka — semuanya automatik sekarang.",
    "testimonials.3.name": "Dr. Tan Wei Ming",
    "testimonials.3.role": "Clinic Owner, Johor Bahru",
    "testimonials.3.quote": "Phone calls dropped by 60% since we deployed the chatbot. Patients get answers about our services, fees, and operating hours instantly. Worth every sen.",
    "testimonials.4.name": "Nurul Aisyah",
    "testimonials.4.role": "Tuition Centre Director, Shah Alam",
    "testimonials.4.quote": "During enrollment season, parents flood us with the same questions. Now the chatbot handles 90% of inquiries automatically — in 3 languages!",
    "testimonials.5.name": "Jason Ong",
    "testimonials.5.role": "Digital Agency CEO, Kuching",
    "testimonials.5.quote": "The white-label feature is perfect for our agency. We resell Onyx AI to 15+ clients, each with their own branding. Multi-tenant architecture just works.",
    "testimonials.6.name": "Priya Devi",
    "testimonials.6.role": "Insurance Agent, Ipoh",
    "testimonials.6.quote": "My clients can now get policy information and quote requests 24/7 through the chatbot. It's like having a virtual assistant that never sleeps.",

    // Integrations
    "integrations.title": "Works Everywhere — One Line of Code",
    "integrations.subtitle": "Embed Onyx AI on any website in seconds. No technical expertise needed — just copy, paste, and your chatbot is live.",
    "integrations.shopify": "Full support",
    "integrations.wordpress": "Plugin ready",
    "integrations.wix": "Embed code",
    "integrations.shopee": "Via website",
    "integrations.custom": "Any website",
    "integrations.react": "NPM package",
    "integrations.codeLabel": "Just one script tag",
    "integrations.codeComment": "Add Onyx AI to your website",
    "integrations.codeDone": "That's it! Your AI chatbot is now live",

    // Blog Preview
    "blog.preview.title": "From Our Blog",
    "blog.preview.subtitle": "Tips & guides to automate your customer support",
    "blog.preview.viewAll": "View All Posts",
    "blog.preview.readMore": "Read more",
    "blog.preview.1.title": "Best AI Chatbot for Malaysian Businesses in 2026",
    "blog.preview.1.desc": "Compare the top AI chatbot platforms for Malaysian SMEs. Find the best solution for e-commerce, F&B, clinics & more.",
    "blog.preview.2.title": "How Much Does a Chatbot Cost in Malaysia?",
    "blog.preview.2.desc": "Complete chatbot pricing guide for Malaysian businesses. Compare free to enterprise plans and calculate ROI.",
    "blog.preview.3.title": "AI Chatbot for Shopee & Lazada Sellers",
    "blog.preview.3.desc": "Learn how Malaysian e-commerce sellers automate order inquiries, returns, and product questions with AI.",

    // Footer
    "footer.product": "Product",
    "footer.company": "Company",
    "footer.support": "Support",
    "footer.about": "About Us",
    "footer.blog": "Blog",
    "footer.docs": "Documentation",
    "footer.contact": "Contact Us",
    "footer.status": "System Status",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.rights": "All rights reserved.",
    "footer.tagline": "AI-powered customer support for Malaysian businesses.",
  },

  zh: {
    // Header
    "nav.features": "功能",
    "nav.pricing": "价格",
    "nav.howItWorks": "使用方法",
    "nav.login": "登录",

    // Hero
    "hero.badge": "🇲🇾 专为马来西亚企业打造 — 免费开始",
    "hero.title1": "认识",
    "hero.title2": "24/7 自动回复客户",
    "hero.description": "AI聊天机器人以英语、BM和中文回答客户 — 即使你在睡觉。上传FAQ，嵌入一行代码，5分钟上线。无需编程。",
    "hero.cta1": "免费开始",
    "hero.cta2": "了解运作方式",
    "hero.chat.title": "Onyx AI 对话",
    "hero.chat.q1": "你们的营业时间是什么？",
    "hero.chat.a1": "我们周一至周五营业，上午9点至下午6点（马来西亚时间）。紧急事务可随时联系AI助手！",
    "hero.chat.q2": "如何退货？",
    "hero.chat.a2": "您可以在购买后30天内发起退货。进入订单历史，点击「申请退货」即可。",
    "hero.trusted": "受马来西亚中小企业信赖",
    "hero.demo.label": "实时演示",
    "hero.demo.prompt": "立即试用 Onyx AI — 随便问！",
    "hero.demo.tryIt": "👇 立即体验 — 与 Onyx AI 对话",
    "hero.demo.inputPlaceholder": "问我任何关于 Onyx AI 的问题...",

    // Stats
    "stats.businesses": "服务企业",
    "stats.messages": "消息已回复",
    "stats.uptime": "正常运行时间",
    "stats.satisfaction": "满意度",

    // How It Works
    "howItWorks.title": "几分钟即可开始",
    "howItWorks.subtitle": "三个简单步骤，用 AI 转型您的客户支持 — 无需编程。",
    "howItWorks.step1.label": "步骤 1",
    "howItWorks.step1.title": "上传常见问题",
    "howItWorks.step1.description": "通过 CSV 导入现有的常见问题知识库或手动添加。AI 立即学习您的业务 — 从产品详情到退货政策。",
    "howItWorks.step2.label": "步骤 2",
    "howItWorks.step2.title": "自定义机器人",
    "howItWorks.step2.description": "设置品牌颜色、标志、欢迎语和 AI 人设。让它成为品牌的自然延伸。",
    "howItWorks.step3.label": "步骤 3",
    "howItWorks.step3.title": "嵌入并上线",
    "howItWorks.step3.description": "将一段代码复制到网站中。AI 聊天机器人即刻上线，多语言全天候服务。",

    // Features
    "features.title": "自动化客服所需的一切",
    "features.subtitle": "为马来西亚企业打造的完整 AI 聊天机器人平台 — 从小店到大企业，无需技术专长。",
    "features.faq.title": "FAQ 驱动 AI",
    "features.faq.description": "导入常见问题知识库，AI 立即学会准确自然地回答客户问题。",
    "features.branding.title": "白标品牌定制",
    "features.branding.description": "自定义颜色、标志和消息以匹配品牌。客户看到您的品牌，而非我们的。",
    "features.streaming.title": "流式响应",
    "features.streaming.description": "通过 Server-Sent Events 实时流式传输回复。客户可看到答案逐字出现。",
    "features.multiTenant.title": "多租户架构",
    "features.multiTenant.description": "每个客户拥有独立数据、唯一API密钥和自定义配置。适合代理商和经销商。",
    "features.embed.title": "轻松嵌入集成",
    "features.embed.description": "只需一段代码即可添加到任何网站。兼容 Shopify、WordPress、Wix 或自定义网站。",
    "features.analytics.title": "分析仪表板",
    "features.analytics.description": "追踪聊天会话、热门问题和回复质量。了解客户最常问的问题。",
    "features.liveAgent.title": "人工客服转接",
    "features.liveAgent.description": "无缝将复杂对话转接给人工客服。团队获得完整上下文和聊天记录。",
    "features.multilingual.title": "多语言支持",
    "features.multilingual.description": "以英语、马来语和中文服务客户。AI 以客户偏好的语言回复。",
    "features.hours.title": "营业时间与自动回复",
    "features.hours.description": "设置营业时间和自动回复消息。客户始终知道何时能获得人工回复。",
    "features.agentWidget.title": "可嵌入客服组件",
    "features.agentWidget.description": "让客服团队从任何系统回复客户。只需一段代码即可将轻量客服控制台嵌入您的 CRM、工单系统或内部工具。",
    "features.notifications.title": "实时通知提醒",
    "features.notifications.description": "不错过任何客户消息。当新对话或消息到达时，获取声音提醒、桌面通知和标签页闪烁提示。",

    // Use Cases
    "useCases.title": "专为马来西亚企业打造",
    "useCases.subtitle": "无论您经营商店、诊所还是科技公司 — Onyx AI 适应您的行业，说客户的语言。",
    "useCases.ecommerce.title": "电子商务与零售",
    "useCases.ecommerce.description": "自动处理产品咨询、订单状态、退货和物流问题。适合需要全天候自动客服的 Shopee 和 Lazada 卖家。",
    "useCases.fnb.title": "餐饮业",
    "useCases.fnb.description": "回答菜单问题、接受预订、分享营业时间。即使厨房已关，依然为客户服务 — 马来西亚餐厅最佳AI聊天机器人。",
    "useCases.services.title": "专业服务",
    "useCases.services.description": "律所、诊所、代理商 — 让 AI 处理预约安排、服务咨询和常见问题。减少爽约和电话咨询。",
    "useCases.education.title": "教育与培训",
    "useCases.education.description": "补习中心、大学和在线课程 — 为家长和学生自动化报名咨询、课程表和费用查询。",

    // Pricing
    "pricing.title": "简单透明的定价",
    "pricing.subtitle": "免费开始，随业务增长扩展。没有隐藏费用。马来西亚中小企业可负担的聊天机器人方案。",
    "pricing.free.name": "免费版",
    "pricing.free.description": "适合体验 AI 客户支持",
    "pricing.free.f1": "每月 100 条消息",
    "pricing.free.f2": "1 个人工客服",
    "pricing.free.f3": "AI 聊天机器人含人设",
    "pricing.free.f4": "基础分析",
    "pricing.free.f5": "社区支持",
    "pricing.free.cta": "免费开始",
    "pricing.starter.name": "入门版",
    "pricing.starter.description": "适合刚起步的小企业",
    "pricing.starter.f1": "每月 5,000 条消息",
    "pricing.starter.f2": "最多 3 个人工客服",
    "pricing.starter.f3": "自定义 AI 人设名称",
    "pricing.starter.f4": "自定义品牌",
    "pricing.starter.f5": "CSV 批量导入",
    "pricing.starter.f6": "优先支持",
    "pricing.starter.f7": "声音与桌面通知",
    "pricing.starter.cta": "立即开始",
    "pricing.pro.name": "专业版",
    "pricing.pro.description": "适合需要更多功能的成长型企业",
    "pricing.pro.f1": "每月 20,000 条消息",
    "pricing.pro.f2": "最多 10 个人工客服",
    "pricing.pro.f3": "高级分析",
    "pricing.pro.f4": "营业时间设置",
    "pricing.pro.f5": "预设回复",
    "pricing.pro.f6": "自定义系统提示",
    "pricing.pro.f7": "声音与桌面通知",
    "pricing.pro.f8": "可嵌入客服组件",
    "pricing.pro.cta": "开始免费试用",
    "pricing.pro.badge": "最受欢迎",
    "pricing.business.name": "商业版",
    "pricing.business.description": "适合高流量需求的大型团队",
    "pricing.business.f1": "每月 50,000 条消息",
    "pricing.business.f2": "无限人工客服",
    "pricing.business.f3": "无限常见问题",
    "pricing.business.f4": "技能路由",
    "pricing.business.f5": "专属支持",
    "pricing.business.f6": "API 速率限制自定义",
    "pricing.business.f7": "声音与桌面通知",
    "pricing.business.f8": "可嵌入客服组件",
    "pricing.business.cta": "联系销售",
    "pricing.perMonth": "/月",
    "pricing.seeFullPricing": "查看完整定价 →",
    "pricing.faq.title": "常见问题",
    "pricing.faq.q1": "之后可以更改套餐吗？",
    "pricing.faq.a1": "可以，您随时可以从控制台升级或降级套餐，更改立即生效。",
    "pricing.faq.q2": "有免费试用吗？",
    "pricing.faq.a2": "免费版永久免费，无需信用卡。付费套餐提供 7 天退款保证。",
    "pricing.faq.q3": "支持哪些付款方式？",
    "pricing.faq.a3": "我们接受信用卡/借记卡及马来西亚 FPX 网上银行。RM 价格基于当前汇率为近似值。",
    "pricing.faq.q4": "超出消息额度怎么办？",
    "pricing.faq.a4": "聊天机器人将暂停至下一个计费周期。您可随时升级以立即恢复服务。",
    "pricing.faq.q5": "可以退款吗？",
    "pricing.faq.a5": "所有付费套餐提供 7 天退款保证。请联系 hello.onyx@cloudforge.com.my。",

    // CTA
    "cta.title": "准备好转型您的客户支持了吗？",
    "cta.subtitle": "加入数百家已使用 Onyx AI 的马来西亚企业 — 以客户说的每种语言取悦他们。",
    "cta.button": "免费开始 — 无需信用卡",

    // Affiliate
    "affiliate.badge": "与 Onyx AI 一起赚钱",
    "affiliate.title": "成为 Onyx AI 合作伙伴，赚取持续佣金",
    "affiliate.description": "加入我们的合作伙伴计划，每次推荐用户订阅即可赚取佣金。无论您是代理商、自媒体人还是技术爱好者，这都是将人脉变现的最简单方式。",
    "affiliate.perk1": "每笔推荐 20% 持续佣金",
    "affiliate.perk2": "实时仪表板追踪推荐与收益",
    "affiliate.perk3": "专属推荐链接和推广素材",
    "affiliate.perk4": "每月支付 — 无最低门槛",
    "affiliate.cta": "加入合作伙伴计划",
    "affiliate.dashboard": "合作伙伴仪表板",
    "affiliate.thisMonth": "本月",
    "affiliate.referrals": "推荐数",
    "affiliate.earnings": "收益",

    // Testimonials
    "testimonials.title": "深受马来西亚企业喜爱",
    "testimonials.subtitle": "看看用户如何评价 Onyx AI 自动化客户支持。",
    "testimonials.1.name": "Sarah Lim",
    "testimonials.1.role": "电商店主, 吉隆坡",
    "testimonials.1.quote": "Onyx AI 减少了我们 70% 的客服工单。Shopee 客户在凌晨 3 点也能获得中英文即时回答。对小团队来说是颠覆性的。",
    "testimonials.2.name": "Ahmad Razak",
    "testimonials.2.role": "餐厅经理, 槟城",
    "testimonials.2.quote": "我们的客户喜欢用马来语提问并立即获得回答。菜单、预订、营业时间 — 现在全自动了。",
    "testimonials.3.name": "Dr. Tan Wei Ming",
    "testimonials.3.role": "诊所老板, 新山",
    "testimonials.3.quote": "部署聊天机器人后电话量减少了60%。患者可以立即获得关于服务、费用和营业时间的答案。",
    "testimonials.4.name": "Nurul Aisyah",
    "testimonials.4.role": "补习中心主任, Shah Alam",
    "testimonials.4.quote": "报名季节家长们大量涌入同样的问题。现在聊天机器人自动处理 90% 的咨询 — 用三种语言！",
    "testimonials.5.name": "Jason Ong",
    "testimonials.5.role": "数字代理商 CEO, 古晋",
    "testimonials.5.quote": "白标功能对我们代理商来说太完美了。我们向 15+ 客户转售 Onyx AI，每个都有自己的品牌。多租户架构非常好用。",
    "testimonials.6.name": "Priya Devi",
    "testimonials.6.role": "保险代理, 怡保",
    "testimonials.6.quote": "我的客户现在可以通过聊天机器人 24/7 获取保单信息和报价请求。就像有一个永不休息的虚拟助手。",

    // Integrations
    "integrations.title": "一行代码，到处可用",
    "integrations.subtitle": "几秒钟内在任何网站嵌入 Onyx AI。无需技术专长 — 复制、粘贴即可上线。",
    "integrations.shopify": "完全支持",
    "integrations.wordpress": "插件就绪",
    "integrations.wix": "嵌入代码",
    "integrations.shopee": "通过网站",
    "integrations.custom": "任何网站",
    "integrations.react": "NPM 包",
    "integrations.codeLabel": "只需一个脚本标签",
    "integrations.codeComment": "将 Onyx AI 添加到您的网站",
    "integrations.codeDone": "完成！您的 AI 聊天机器人现已上线",

    // Blog Preview
    "blog.preview.title": "博客文章",
    "blog.preview.subtitle": "自动化客户支持的提示与指南",
    "blog.preview.viewAll": "查看所有文章",
    "blog.preview.readMore": "阅读更多",
    "blog.preview.1.title": "2026年马来西亚企业最佳AI聊天机器人",
    "blog.preview.1.desc": "比较马来西亚中小企业顶级AI聊天机器人平台。找到最适合电商、餐饮、诊所等行业的解决方案。",
    "blog.preview.2.title": "马来西亚聊天机器人价格是多少？",
    "blog.preview.2.desc": "马来西亚企业完整的聊天机器人价格指南。比较免费到企业级方案并计算投资回报率。",
    "blog.preview.3.title": "Shopee和Lazada卖家的AI聊天机器人",
    "blog.preview.3.desc": "了解马来西亚电商卖家如何使用AI自动化处理订单查询、退货和产品问题。",

    // Footer
    "footer.product": "产品",
    "footer.company": "公司",
    "footer.support": "支持",
    "footer.about": "关于我们",
    "footer.blog": "博客",
    "footer.docs": "文档",
    "footer.contact": "联系我们",
    "footer.status": "系统状态",
    "footer.privacy": "隐私政策",
    "footer.terms": "服务条款",
    "footer.rights": "版权所有。",
    "footer.tagline": "为马来西亚企业打造的 AI 客户支持。",
  },

  ms: {
    // Header
    "nav.features": "Ciri-ciri",
    "nav.pricing": "Harga",
    "nav.howItWorks": "Cara Guna",
    "nav.login": "Log Masuk",

    // Hero
    "hero.badge": "🇲🇾 Dibina untuk Perniagaan Malaysia — Mula Percuma",
    "hero.title1": "Perkenalkan",
    "hero.title2": "Balas Customer 24/7, Automatik",
    "hero.description": "Chatbot AI yang jawab pelanggan dalam English, BM & 中文 — walaupun anda tidur. Upload FAQ, embed satu script tag, dan go live dalam 5 minit. Tak perlu coding.",
    "hero.cta1": "Mula Percuma",
    "hero.cta2": "Lihat Cara Ia Berfungsi",
    "hero.chat.title": "Onyx AI Chat",
    "hero.chat.q1": "Apakah waktu operasi perniagaan anda?",
    "hero.chat.a1": "Kami buka Isnin hingga Jumaat, 9 pagi - 6 petang (MYT). Untuk urusan mendesak, pembantu AI kami sedia 24/7!",
    "hero.chat.q2": "Bagaimana cara memulangkan produk?",
    "hero.chat.a2": "Anda boleh memulakan pemulangan dalam 30 hari selepas pembelian. Pergi ke sejarah pesanan dan klik \"Minta Pemulangan\".",
    "hero.trusted": "Dipercayai oleh PKS di seluruh Malaysia",
    "hero.demo.label": "Demo Langsung",
    "hero.demo.prompt": "Cuba bercakap dengan Onyx AI sekarang — tanya apa sahaja!",
    "hero.demo.tryIt": "👇 Cuba sekarang — bercakap dengan Onyx AI",
    "hero.demo.inputPlaceholder": "Tanya saya apa sahaja tentang Onyx AI...",

    // Stats
    "stats.businesses": "Perniagaan Dilayani",
    "stats.messages": "Mesej Dijawab",
    "stats.uptime": "Masa Aktif",
    "stats.satisfaction": "Kadar Kepuasan",

    // How It Works
    "howItWorks.title": "Mula dalam Beberapa Minit",
    "howItWorks.subtitle": "Tiga langkah mudah untuk mengubah sokongan pelanggan anda dengan AI — tanpa perlu kod.",
    "howItWorks.step1.label": "Langkah 1",
    "howItWorks.step1.title": "Muat Naik FAQ Anda",
    "howItWorks.step1.description": "Import pangkalan FAQ sedia ada melalui CSV atau tambah secara manual. AI kami belajar perniagaan anda serta-merta — dari butiran produk hingga polisi pemulangan.",
    "howItWorks.step2.label": "Langkah 2",
    "howItWorks.step2.title": "Sesuaikan Bot Anda",
    "howItWorks.step2.description": "Tetapkan warna jenama, logo, mesej alu-aluan dan persona AI. Jadikan ia sebahagian semula jadi identiti jenama anda.",
    "howItWorks.step3.label": "Langkah 3",
    "howItWorks.step3.title": "Benamkan & Lancarkan",
    "howItWorks.step3.description": "Salin satu tag skrip ke laman web anda. Chatbot AI anda aktif dan sedia melayani pelanggan 24/7 dalam pelbagai bahasa.",

    // Features
    "features.title": "Semua yang Anda Perlukan untuk Automasikan Sokongan",
    "features.subtitle": "Platform chatbot AI lengkap dibina untuk perniagaan Malaysia — dari kedai runcit hingga enterprise. Tanpa kepakaran teknikal.",
    "features.faq.title": "AI Berkuasa FAQ",
    "features.faq.description": "Masukkan pangkalan FAQ dan AI kami serta-merta belajar menjawab soalan pelanggan dengan tepat dan semula jadi.",
    "features.branding.title": "Penjenamaan White-Label",
    "features.branding.description": "Sesuaikan warna, logo dan mesej untuk sepadan dengan jenama anda. Pelanggan nampak jenama anda, bukan kami.",
    "features.streaming.title": "Respons Strim",
    "features.streaming.description": "Respons AI masa nyata dengan Server-Sent Events. Pelanggan melihat jawapan muncul perkataan demi perkataan.",
    "features.multiTenant.title": "Seni Bina Multi-Tenant",
    "features.multiTenant.description": "Setiap pelanggan mendapat data terpencil, kunci API unik dan konfigurasi tersuai. Sesuai untuk agensi dan penjual semula.",
    "features.embed.title": "Integrasi Benam Mudah",
    "features.embed.description": "Tambah chatbot ke mana-mana laman web dengan satu tag skrip. Berfungsi dengan Shopify, WordPress, Wix atau laman tersuai.",
    "features.analytics.title": "Papan Pemuka Analitik",
    "features.analytics.description": "Jejaki sesi chat, soalan popular dan kualiti respons. Fahami apa yang pelanggan anda paling banyak tanya.",
    "features.liveAgent.title": "Pindah ke Ejen Langsung",
    "features.liveAgent.description": "Pindahkan perbualan kompleks kepada ejen manusia dengan lancar. Pasukan anda mendapat konteks penuh dan sejarah chat.",
    "features.multilingual.title": "Sokongan Pelbagai Bahasa",
    "features.multilingual.description": "Layani pelanggan dalam Bahasa Inggeris, Melayu dan Cina. AI menjawab dalam bahasa pilihan pelanggan.",
    "features.hours.title": "Waktu Operasi & Auto-Balas",
    "features.hours.description": "Tetapkan waktu operasi dan mesej auto-balas. Pelanggan sentiasa tahu bila boleh mendapat respons manusia.",
    "features.agentWidget.title": "Widget Ejen Boleh Benam",
    "features.agentWidget.description": "Biarkan pasukan CS anda membalas pelanggan dari mana-mana sistem. Benamkan konsol ejen ringan ke dalam CRM, meja bantuan atau alat dalaman anda dengan satu tag skrip.",
    "features.notifications.title": "Pemberitahuan Masa Nyata",
    "features.notifications.description": "Jangan lepaskan sebarang mesej pelanggan. Dapatkan amaran bunyi, pemberitahuan desktop dan kelipan tajuk tab apabila chat atau mesej baharu tiba.",

    // Use Cases
    "useCases.title": "Dibina untuk Perniagaan Malaysia",
    "useCases.subtitle": "Sama ada anda jalankan kedai, klinik atau startup teknologi — Onyx AI menyesuaikan diri dengan industri anda dan bercakap bahasa pelanggan anda.",
    "useCases.ecommerce.title": "E-Dagang & Runcit",
    "useCases.ecommerce.description": "Kendalikan pertanyaan produk, status pesanan, pemulangan dan soalan penghantaran secara automatik. Sesuai untuk penjual Shopee dan Lazada yang perlukan sokongan pelanggan automatik 24/7.",
    "useCases.fnb.title": "F&B & Restoran",
    "useCases.fnb.description": "Jawab soalan menu, terima tempahan dan kongsikan waktu operasi. Layani pelanggan walaupun dapur sudah tutup — chatbot AI terbaik untuk restoran di Malaysia.",
    "useCases.services.title": "Perkhidmatan Profesional",
    "useCases.services.description": "Firma guaman, klinik, agensi — biarkan AI mengendalikan penjadualan temu janji, pertanyaan perkhidmatan dan FAQ. Kurangkan ketidakhadiran dan panggilan telefon.",
    "useCases.education.title": "Pendidikan & Latihan",
    "useCases.education.description": "Pusat tuisyen, universiti dan kursus dalam talian — automatikkan pertanyaan pendaftaran, jadual kelas dan pertanyaan yuran untuk ibu bapa dan pelajar.",

    // Pricing
    "pricing.title": "Harga Mudah & Telus",
    "pricing.subtitle": "Mula percuma dan skala mengikut pertumbuhan. Tiada caj tersembunyi. Penyelesaian chatbot mampu milik untuk PKS Malaysia.",
    "pricing.free.name": "Percuma",
    "pricing.free.description": "Sesuai untuk mencuba sokongan berkuasa AI",
    "pricing.free.f1": "100 mesej / bulan",
    "pricing.free.f2": "1 ejen langsung",
    "pricing.free.f3": "Chatbot AI dengan persona",
    "pricing.free.f4": "Analitik asas",
    "pricing.free.f5": "Sokongan komuniti",
    "pricing.free.cta": "Mula Sekarang",
    "pricing.starter.name": "Permulaan",
    "pricing.starter.description": "Untuk perniagaan kecil yang baru bermula",
    "pricing.starter.f1": "5,000 mesej / bulan",
    "pricing.starter.f2": "Sehingga 3 ejen langsung",
    "pricing.starter.f3": "Nama persona AI tersuai",
    "pricing.starter.f4": "Penjenamaan tersuai",
    "pricing.starter.f5": "Import CSV pukal",
    "pricing.starter.f6": "Sokongan keutamaan",
    "pricing.starter.f7": "Pemberitahuan bunyi & desktop",
    "pricing.starter.cta": "Mula Sekarang",
    "pricing.pro.name": "Pro",
    "pricing.pro.description": "Untuk perniagaan berkembang yang perlu lebih",
    "pricing.pro.f1": "20,000 mesej / bulan",
    "pricing.pro.f2": "Sehingga 10 ejen langsung",
    "pricing.pro.f3": "Analitik lanjutan",
    "pricing.pro.f4": "Waktu perniagaan",
    "pricing.pro.f5": "Respons siap sedia",
    "pricing.pro.f6": "Prompt sistem tersuai",
    "pricing.pro.f7": "Pemberitahuan bunyi & desktop",
    "pricing.pro.f8": "Widget ejen boleh benam",
    "pricing.pro.cta": "Mula Percubaan Percuma",
    "pricing.pro.badge": "Paling Popular",
    "pricing.business.name": "Bisnes",
    "pricing.business.description": "Untuk pasukan besar dengan keperluan volume tinggi",
    "pricing.business.f1": "50,000 mesej / bulan",
    "pricing.business.f2": "Ejen langsung tanpa had",
    "pricing.business.f3": "FAQ tanpa had",
    "pricing.business.f4": "Penghalaan berasaskan kemahiran",
    "pricing.business.f5": "Sokongan dedikasi",
    "pricing.business.f6": "Penyesuaian had kadar API",
    "pricing.business.f7": "Pemberitahuan bunyi & desktop",
    "pricing.business.f8": "Widget ejen boleh benam",
    "pricing.business.cta": "Hubungi Jualan",
    "pricing.perMonth": "/bulan",
    "pricing.seeFullPricing": "Lihat harga penuh →",
    "pricing.faq.title": "Soalan Lazim",
    "pricing.faq.q1": "Bolehkah saya tukar pelan kemudian?",
    "pricing.faq.a1": "Ya, anda boleh naik taraf atau turun taraf pelan pada bila-bila masa dari papan pemuka anda. Perubahan berkuat kuasa serta-merta.",
    "pricing.faq.q2": "Adakah terdapat percubaan percuma?",
    "pricing.faq.a2": "Pelan Percuma adalah percuma selama-lamanya — tiada kad kredit diperlukan. Pelan berbayar termasuk jaminan wang dikembalikan 7 hari.",
    "pricing.faq.q3": "Kaedah pembayaran apa yang diterima?",
    "pricing.faq.a3": "Kami menerima kad kredit/debit dan perbankan dalam talian FPX untuk pelanggan Malaysia. Harga dalam RM adalah anggaran berdasarkan kadar pertukaran semasa.",
    "pricing.faq.q4": "Apa yang berlaku jika melebihi had mesej?",
    "pricing.faq.a4": "Chatbot anda akan dijeda sehingga kitaran bil seterusnya. Anda boleh naik taraf pada bila-bila masa untuk memulihkan perkhidmatan segera.",
    "pricing.faq.q5": "Adakah anda menawarkan bayaran balik?",
    "pricing.faq.a5": "Ya, kami menawarkan jaminan wang dikembalikan 7 hari untuk semua pelan berbayar. Hubungi kami di hello.onyx@cloudforge.com.my.",

    // CTA
    "cta.title": "Sedia Mengubah Sokongan Pelanggan Anda?",
    "cta.subtitle": "Sertai ratusan perniagaan Malaysia yang sudah gunakan Onyx AI untuk menggembirakan pelanggan — dalam setiap bahasa yang mereka tutur.",
    "cta.button": "Mula Percuma — Tanpa Kad Kredit",

    // Affiliate
    "affiliate.badge": "Jana Pendapatan dengan Onyx AI",
    "affiliate.title": "Jadi Rakan Kongsi Onyx AI & Jana Komisen Berulang",
    "affiliate.description": "Sertai program rakan kongsi kami dan jana pendapatan setiap kali seseorang yang anda rujuk melanggan. Cara paling mudah untuk monetize rangkaian anda.",
    "affiliate.perk1": "Komisen berulang 20% untuk setiap rujukan",
    "affiliate.perk2": "Papan pemuka masa nyata untuk jejaki rujukan & pendapatan",
    "affiliate.perk3": "Pautan rujukan tersuai dan bahan promosi",
    "affiliate.perk4": "Bayaran bulanan — tiada ambang minimum",
    "affiliate.cta": "Sertai Program Rakan Kongsi",
    "affiliate.dashboard": "Papan Pemuka Rakan Kongsi",
    "affiliate.thisMonth": "Bulan Ini",
    "affiliate.referrals": "Rujukan",
    "affiliate.earnings": "Pendapatan",

    // Testimonials
    "testimonials.title": "Disayangi Perniagaan Malaysia",
    "testimonials.subtitle": "Lihat apa kata pengguna kami tentang automasi sokongan pelanggan dengan Onyx AI.",
    "testimonials.1.name": "Sarah Lim",
    "testimonials.1.role": "Pemilik Kedai E-Dagang, KL",
    "testimonials.1.quote": "Onyx AI kurangkan tiket sokongan kami sebanyak 70%. Pelanggan Shopee dapat jawapan segera dalam Cina dan Inggeris — walaupun pukul 3 pagi.",
    "testimonials.2.name": "Ahmad Razak",
    "testimonials.2.role": "Pengurus Restoran, Pulau Pinang",
    "testimonials.2.quote": "Pelanggan kami suka boleh tanya soalan dalam BM dan dapat jawapan segera. Menu, reservasi, waktu buka — semuanya automatik sekarang.",
    "testimonials.3.name": "Dr. Tan Wei Ming",
    "testimonials.3.role": "Pemilik Klinik, Johor Bahru",
    "testimonials.3.quote": "Panggilan telefon berkurang 60% sejak kami gunakan chatbot. Pesakit dapat jawapan tentang perkhidmatan, yuran dan waktu operasi dengan segera.",
    "testimonials.4.name": "Nurul Aisyah",
    "testimonials.4.role": "Pengarah Pusat Tuisyen, Shah Alam",
    "testimonials.4.quote": "Musim pendaftaran, ibu bapa banjiri kami dengan soalan sama. Kini chatbot kendalikan 90% pertanyaan secara automatik — dalam 3 bahasa!",
    "testimonials.5.name": "Jason Ong",
    "testimonials.5.role": "CEO Agensi Digital, Kuching",
    "testimonials.5.quote": "Ciri white-label sempurna untuk agensi kami. Kami jual semula Onyx AI kepada 15+ pelanggan, setiap satu dengan penjenamaan sendiri.",
    "testimonials.6.name": "Priya Devi",
    "testimonials.6.role": "Ejen Insurans, Ipoh",
    "testimonials.6.quote": "Pelanggan saya kini boleh dapatkan maklumat polisi dan permintaan sebut harga 24/7 melalui chatbot. Macam ada pembantu maya yang tak pernah tidur.",

    // Integrations
    "integrations.title": "Berfungsi Di Mana-mana — Satu Baris Kod",
    "integrations.subtitle": "Benamkan Onyx AI di mana-mana laman web dalam beberapa saat. Tanpa kepakaran teknikal — salin, tampal dan chatbot anda aktif.",
    "integrations.shopify": "Sokongan penuh",
    "integrations.wordpress": "Plugin sedia",
    "integrations.wix": "Kod benam",
    "integrations.shopee": "Melalui laman",
    "integrations.custom": "Mana-mana laman",
    "integrations.react": "Pakej NPM",
    "integrations.codeLabel": "Hanya satu tag skrip",
    "integrations.codeComment": "Tambah Onyx AI ke laman web anda",
    "integrations.codeDone": "Siap! Chatbot AI anda kini aktif",

    // Blog Preview
    "blog.preview.title": "Dari Blog Kami",
    "blog.preview.subtitle": "Tips & panduan untuk automasikan sokongan pelanggan anda",
    "blog.preview.viewAll": "Lihat Semua Artikel",
    "blog.preview.readMore": "Baca lagi",
    "blog.preview.1.title": "Chatbot AI Terbaik untuk Perniagaan Malaysia 2026",
    "blog.preview.1.desc": "Bandingkan platform chatbot AI terbaik untuk PKS Malaysia. Cari penyelesaian terbaik untuk e-dagang, F&B, klinik & lagi.",
    "blog.preview.2.title": "Berapakah Kos Chatbot di Malaysia?",
    "blog.preview.2.desc": "Panduan harga chatbot lengkap untuk perniagaan Malaysia. Bandingkan pelan percuma hingga enterprise dan kira ROI.",
    "blog.preview.3.title": "Chatbot AI untuk Penjual Shopee & Lazada",
    "blog.preview.3.desc": "Ketahui cara penjual e-dagang Malaysia automasikan pertanyaan pesanan, pemulangan dan soalan produk dengan AI.",

    // Footer
    "footer.product": "Produk",
    "footer.company": "Syarikat",
    "footer.support": "Sokongan",
    "footer.about": "Tentang Kami",
    "footer.blog": "Blog",
    "footer.docs": "Dokumentasi",
    "footer.contact": "Hubungi Kami",
    "footer.status": "Status Sistem",
    "footer.privacy": "Dasar Privasi",
    "footer.terms": "Terma Perkhidmatan",
    "footer.rights": "Hak cipta terpelihara.",
    "footer.tagline": "Sokongan pelanggan berkuasa AI untuk perniagaan Malaysia.",
  },
};

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<Locale>(() => {
    try {
      const saved = localStorage.getItem("onyx-locale") as Locale;
      return saved && translations[saved] ? saved : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    localStorage.setItem("onyx-locale", locale);
    document.documentElement.lang = locale === "zh" ? "zh-Hans" : locale;
  }, [locale]);

  const t = (key: string): string => {
    return translations[locale]?.[key] || translations.en[key] || key;
  };

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}
